/**
 * BlockRenderer — resolves a UIBlockEnvelope to the correct React component.
 *
 * Used by MessageList to keep rendering logic out of the message loop.
 */

import type { ErrorFallbackProps } from '@commander/ui';
import { ErrorBoundary } from '@commander/ui';
import { motion } from 'motion/react';
import { Suspense } from 'react';
import type { UIBlockEnvelope, UIBlockType } from './blocks/types.js';
import { ErrorCard } from './components/ErrorCard.js';
import { BLOCK_REGISTRY } from './registry.js';

function BlockErrorFallback(_props: ErrorFallbackProps) {
  return <ErrorCard message="Something went wrong — try again" />;
}

export function BlockRenderer({
  envelope,
  toolCallId,
  preSelectedId,
}: {
  envelope: UIBlockEnvelope;
  toolCallId?: string;
  preSelectedId?: string | null;
}) {
  if (envelope.type === 'error') {
    return <ErrorCard message={envelope.message} />;
  }

  const entry = BLOCK_REGISTRY[envelope.type as UIBlockType];
  if (!entry) return null;

  if (envelope.loading) {
    return <entry.skeleton partialData={envelope.partialData} />;
  }

  const data = 'data' in envelope ? envelope.data : undefined;
  const rendered = (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.15 }}
    >
      <entry.component data={data} toolCallId={toolCallId} preSelectedId={preSelectedId} />
    </motion.div>
  );

  return (
    <ErrorBoundary fallback={BlockErrorFallback} featureName={entry.displayName}>
      {entry.lazy ? <Suspense fallback={<entry.skeleton />}>{rendered}</Suspense> : rendered}
    </ErrorBoundary>
  );
}
